import React from 'react'
import type { RouteProps } from 'react-router'
import { useLocation } from 'react-router'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits, getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import AuthRoute from 'components/App/Routes/AuthRoute'

interface Props extends RouteProps {
  showPopup?: boolean
  loader?: React.ReactNode
  accountAccessModalInitialMode?: App.UiAccountModalMode
}

/**
 * Route guard for LuxPlus+ member-only pages.
 *
 * This protects routes that should only
 * be accessible to paying LuxPlus+ members (not free preview members).
 *
 * Access granted to:
 * - Paid LuxPlus+ members (can redeem benefits)
 * - Pending renewal members
 * - Users with an orderId (post-purchase flow, membership being confirmed)
 *
 * Access denied to:
 * - Free preview members (redirected to landing page)
 * - Non-members (redirected to landing page)
 */
function LuxPlusMemberRoute(props: Props) {
  const { component: Component, ...rest } = props
  const location = useLocation()

  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const isPendingRenewalLuxPlusMember = useAppSelector(getIsPendingRenewalLuxPlusMember)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  // Check if this is a post-purchase redirect (has orderId)
  // In this case, we allow access and let the account page handle the loading/fetching
  const searchParams = new URLSearchParams(location.search)
  const hasOrderId = searchParams.has('orderId')

  const canAccessPage = canRedeemLuxPlusBenefits || isPendingRenewalLuxPlusMember || hasOrderId

  if (!canAccessPage) {
    return <RedirectWithStatus
      code={302}
      to={`/${regionCode.toLowerCase()}/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`}
    />
  }

  // Use AuthRoute to also ensure user is logged in
  return <AuthRoute {...rest} component={Component} />
}

export default LuxPlusMemberRoute
